import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";

import { colors } from "../../../theme/palette";
import { createEvent } from "../../../api/events";
import { Group, UserInfo } from "../../../types";
import APP_ROUTES from "../../../routers/routeConstants";

import EventStepper from "./EventStepper";
import FillData from "./FillData";
import DateAndTime from "./DateAndTime";
import SelectParticipants from "./SelectParticipants";
import { CreateEventProvider, useCreateEventContext } from "./cxt";

const CreateEventSteps = () => {
  const { state, setState } = useCreateEventContext();

  const { t } = useTranslation();
  const [step, setStep] = useState<number>(0);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const navigate = useNavigate();
  const { eventType } = useParams();

  const fillDataRef = useRef<{
    validateForm: () => boolean;
    formData: FormData;
    handleSubmit?: () => void;
  } | null>(null);

  const dateAndTimeRef = useRef<{
    isFormValid: boolean;
    date: Date;
    time: Date;
    duration: number;
    getFormattedDateTime: () => void;
  } | null>(null);

  const handleBack = () => {
    if (step > 0) setStep((prevStep) => --prevStep);
  };

  const handleNext = async () => {
    if (step === 0 && fillDataRef.current?.validateForm()) {
      const formData = fillDataRef.current.formData;
      setState((prevState) => ({ ...prevState, ...formData }));
      setStep((prevStep) => prevStep + 1);
    } else if (step === 1 && dateAndTimeRef.current?.isFormValid) {
      if (dateAndTimeRef.current) {
        const formattedDateTime = dateAndTimeRef.current.getFormattedDateTime();
        const selectedDate = dateAndTimeRef.current.date;
        const selectedTime = dateAndTimeRef.current.time;
        const selectedDuration = dateAndTimeRef.current.duration;
        // @ts-ignore
        setState((prevState) => ({
          ...prevState,
          date: selectedDate,
          time: selectedTime,
          duration: selectedDuration,
          formattedDateTime,
        }));
      }

      setStep((prevStep) => prevStep + 1);
    } else if (step === 2) {
      const requestBody = (() => {
        return {
          name: state.eventName,
          privacy: state.matchPrivacy,
          type: eventType === "friendly-match" ? "FRIENDLY" : "TRAINING",
          duration: state.duration,
          location: [state.location?.lat, state.location?.lng],
          description: state.descriptionRules,
          noPlayers: Math.max(...state.numberOfPlayers),
          startDate: state?.formattedDateTime,
          isPlaying: false,
          locationSubtitle: state.location?.formattedAddress,
          invitedPlayers: state.selectedEntities.map((user: UserInfo) => user.id),
          invitedGroups: state.selectedGroups.map((group: Group) => group.id),
        };
      })();
      try {
        setIsRequesting(true);
        const res = await createEvent(requestBody);
        setIsRequesting(false);
        if (res.message === "success") {
          navigate(APP_ROUTES.EVENT_CREATED, {
            state: { res },
          });
        } else toast.error(t("pages.events.createEvent.errorCreatingEvent"));
      } catch (error) {
        setIsRequesting(false);
      }
    }
  };

  let content;
  switch (step) {
    case 0:
      content = <FillData ref={fillDataRef} />;
      break;
    case 1:
      content = <DateAndTime ref={dateAndTimeRef} />;
      break;
    case 2:
      content = <SelectParticipants />;
      break;

    default:
      break;
  }
  return (
    <Box
      sx={{
        padding: "36px 0",
        background: colors.White,
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        width: "870px",
        overflow: "hidden",
        position: "relative",
        textAlign: "center",
      }}
    >
      <Typography
        variant="h4ExtraBold"
        sx={{
          padding: "0 24px 24px",
          borderBottom: `1px solid ${colors.Gray50}`,
          fontWeight: 700,
          textAlign: "left",
          marginBottom: "16px",
        }}
      >
        {t("pages.events.createEvent.createNewEvent")}{" "}
        <Typography variant="h4ExtraBold" sx={{ color: colors.Green100, fontWeight: 700, display: "inline" }}>
          {t(`pages.events.createEvent.${eventType}`)}
        </Typography>
      </Typography>
      <EventStepper step={step} />
      <Box sx={{ padding: "0 24px" }}>{content}</Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
          padding: "0 24px",
        }}
      >
        <Button
          variant="outlined-sm"
          onClick={handleBack}
          disabled={step === 0}
          sx={{
            fontFamily: "HeadingNowVarTrial",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
            minWidth: "120px",
          }}
        >
          {t("pages.events.createEvent.back")}
        </Button>
        <LoadingButton
          variant="primary-sm"
          onClick={handleNext}
          loading={isRequesting}
          disabled={
            state.matchPrivacy === "CLOSED" &&
            step === 2 &&
            state.selectedEntities.length === 0 &&
            state.selectedGroups.length === 0
          }
          sx={{
            fontFamily: "HeadingNowVarTrial",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
            minWidth: "120px",
          }}
        >
          {step < 2 ? t("pages.events.createEvent.next") : t("pages.events.createEvent.create")}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default function WrappedCreateEventSteps() {
  return (
    <CreateEventProvider>
      <CreateEventSteps />
    </CreateEventProvider>
  );
}
